import { useContext, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, Eye } from "lucide-react";
import Heading from "../../../shared_components/Heading/Heading";
import PaginationGroup from "../../../shared_components/PaginationGroup/PaginationGroup";
import UserData from "../../../services/UserData";
import AIPreEnrichmentService from "../../../services/AIPreEnrichmentService";
import layout from "../IngestionBatches/IngestionBatches.module.css";
import style from "./PreEnrichmentRuns.module.css";

const RunDocumentsPage = ({ runId }) => {
  const userData = useContext(UserData);
  const location = useLocation();
  const [page, setPage] = useState(location.state?.documentsPage || 1);
  const [pageSize, setPageSize] = useState(location.state?.documentsPageSize || 10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    setData(null);
    if (!userData) return () => { active = false; };
    AIPreEnrichmentService.getRunDocuments(userData, runId, page, pageSize)
      .then(result => { if (active) setData(result); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userData, runId, page, pageSize, retry]);

  return (
    <section className={layout.container} aria-label="Run Documents" aria-busy={loading}>
      <div>
        <Link className="btn btn-outline-secondary btn-sm mb-3" to="/intelligence/pre-enrichment-runs" state={location.state}>
          <ArrowLeft size={18} aria-hidden="true" /> Back to Pre-Enrichment Runs
        </Link>
        <Heading>Run Documents</Heading>
        <div className={style.runId}>Run: {runId}</div>
      </div>
      <div className={layout.loadingSlot}>
        {loading && <div className={layout.loadingBar} role="progressbar" aria-label="Loading run documents" />}
      </div>
      <div className={layout.content}>
        {error && <div role="alert">Unable to load run documents. <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setRetry(value => value + 1)}>Retry</button></div>}
        {data && <table className={`${style.table} ${style.documentsTable}`}>
          <thead><tr>
            <th scope="col">Document name</th>
            <th scope="col">Previous decision</th>
            <th scope="col">Proposed decision</th>
            <th scope="col">Reason code</th>
            <th scope="col">View</th>
          </tr></thead>
          <tbody>
            {data.documents.map(document => <tr key={document.evaluation_result_id || document.document_id}>
              <td>{document.document_name ?? "—"}</td>
              <td>{document.previous_decision ?? "—"}</td>
              <td>{document.proposed_decision ?? "—"}</td>
              <td title={document.reason || undefined}>{document.reason_code ?? "—"}</td>
              <td><Link to={`/intelligence/pre-enrichment-runs/${encodeURIComponent(runId)}/documents/${encodeURIComponent(document.document_id)}`} state={{ ...location.state, documentsPage: page, documentsPageSize: pageSize }} className={style.viewDocument} aria-label={`View ${document.document_name || "document"}`} title="View document"><Eye size={22} aria-hidden="true" /></Link></td>
            </tr>)}
            {data.documents.length === 0 && <tr><td colSpan={5}>No documents found for this run.</td></tr>}
          </tbody>
        </table>}
      </div>
      {data && !error && <div className={layout.pagination}>
        <PaginationGroup activePage={page} numberOfPages={Math.max(1, data.pagination.total_pages)} updatePageClicked={setPage} />
        <label className={layout.pageSize}>Page size
          <select className="form-select form-select-sm" value={pageSize} onChange={event => { setPageSize(Number(event.target.value)); setPage(1); }}>
            {[10, 25, 50, 100].map(size => <option key={size} value={size}>{size}</option>)}
          </select>
        </label>
      </div>}
    </section>
  );
};

const RunDocuments = () => {
  const { runId } = useParams();
  return <RunDocumentsPage key={runId} runId={runId} />;
};

export default RunDocuments;
